import "./styles.css";
import { useTranslation } from "react-i18next";
import CrewShiftsModule from "./CrewShiftsModule";
import ButtonDatePicker from "../ButtonDatePicker";
import { useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as Plus } from "../../assets/plus.svg";
import { ReactComponent as Download } from "../../assets/download.svg";
import moment from "moment-timezone";
import {
  ANALYTICS_EVENTS,
  MIN_DATE,
  SEARCH_QUERY_PLACEHOLDERS,
} from "../../constants";
import {
  formatLongDate,
  formatMediumDate,
  getByFieldValue,
  getTimezoneFromUser,
  isBlank,
  isEmptyList,
  isNullOrUndefined,
  isSearchQueryMatch,
  logAnalyticsEvent,
  sortByField,
} from "../../utils";
import { useMainContext, useMainDispatchContext } from "../../MainContext";
import { getDepartments, getTurnaroundsSummary, getUsers } from "../../api";
import { ReactComponent as Upload } from "../../assets/upload.svg";
import TitleBadge from "../TitleBadge";
import { getUserInfo } from "../../userUtils";
import {
  getFileUpload,
  importPreviewCrewShifts,
  searchCrewShifts,
  uploadCrewShiftsFile,
} from "../../crewShiftsApi";
import {
  useCrewShiftsContext,
  useCrewShiftsDispatchContext,
} from "../../CrewShiftsContext";
import CrewShiftPreviewModal from "./CrewShiftPreviewModal";
import csvTemplate from "../../static/sample_crew_shifts.csv";
import CrewShiftExportModal from "./CrewShiftExportModal";
import { isValidDateString, SEARCH_PARAM_DATE_FORMAT } from "../../dateUtils";
import { useSearchParams } from "react-router-dom";
import ButtonFilter from "../ButtonFilter";
import { SettingNames, SettingsManager } from "../../SettingsManager";

function CrewShifts() {
  const { t } = useTranslation();

  const dispatch = useMainDispatchContext();
  const crewShiftsDispatch = useCrewShiftsDispatchContext();
  const mainContext = useMainContext();
  const crewShiftsContext = useCrewShiftsContext();
  const {
    users,
    currentUser,
    departments,
    turnaroundsSummary,
    positions,
    searchQuery,
  } = mainContext;
  const {
    globalError,
    globalErrorList,
    fileUpload,
    crewShifts,
    crewShiftsImportPreview,
  } = crewShiftsContext;

  const airportTimezone = getTimezoneFromUser(currentUser);
  const [searchParams, setSearchParams] = useSearchParams();
  const qsDate = searchParams.get("date");
  const mQsDate = isValidDateString(qsDate)
    ? moment(qsDate).tz(airportTimezone).startOf("day")
    : null;
  const mToday = moment().tz(airportTimezone).startOf("day");

  const [selectedDate, setSelectedDate] = useState(
    !isNullOrUndefined(mQsDate) ? mQsDate : mToday
  );
  const [isUploading, setIsUploading] = useState(false);
  const [importFileUploadUuid, setImportFileUploadUuid] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);

  const currentDepartmentFilters = SettingsManager.getSetting(
    SettingNames.CREW_SHIFT_FILTER_DEPARTMENTS.name
  );
  const [selectedOptions, setSelectedOptions] = useState(
    !isBlank(currentDepartmentFilters)
      ? currentDepartmentFilters.split(",")
      : []
  );

  useEffect(() => {
    dispatch({
      type: "setSearchQuery",
      value: {
        searchTerm: "",
        searchPlaceholder: SEARCH_QUERY_PLACEHOLDERS.SEARCH_CREW,
      },
    });
  }, [dispatch]);

  useEffect(() => {
    sortByField(departments, "name");
    // if list contains a default department, move it to the front
    const defaultDepartment = getByFieldValue(departments, "name", "Default");
    if (!isNullOrUndefined(defaultDepartment)) {
      const defaultDepartmentIndex = departments.indexOf(defaultDepartment);
      if (defaultDepartmentIndex > 0) {
        departments.splice(defaultDepartmentIndex, 1);
        departments.unshift(defaultDepartment);
      }
    }
    if (!isEmptyList(departments)) {
      // Remove any unknown departments from the filter
      const departmentIds = departments.map((i) => i.uuid);
      setSelectedOptions((prev) => {
        const cleanedDepartments = [];
        for (let i = 0; i < prev.length; i++) {
          const deptUuidToCheck = prev[i];
          if (departmentIds.includes(deptUuidToCheck)) {
            cleanedDepartments.push(deptUuidToCheck);
          }
        }
        return cleanedDepartments;
      });
    }
  }, [departments]);

  const [crewShiftsToRender, crewShiftsByUserUuid, crewShiftDepartments] =
    useMemo(() => {
      const crewShiftsMap = {};
      const crewShiftsList = [];
      const departmentsMap = {};
      if (!isEmptyList(crewShifts) && !isEmptyList(users)) {
        for (let i = 0; i < crewShifts.length; i++) {
          const crewShift = crewShifts[i];
          const crewShiftUser = getByFieldValue(
            users,
            "uuid",
            crewShift.userUuid
          );
          if (!isNullOrUndefined(crewShiftUser)) {
            const userInfo = getUserInfo(
              crewShiftUser,
              turnaroundsSummary,
              positions
            );
            const crewShiftInfo = {
              ...crewShift,
              department: crewShift.department,
              userInfo: userInfo,
            };

            // Check for selected day because we don't want to show overnight shifts
            // that start day before and end on the selected day
            const mCrewShiftStartDay = moment(crewShift.startTime)
              .tz(airportTimezone)
              .startOf("day");
            const mSelectedDate = moment(selectedDate)
              .tz(airportTimezone)
              .startOf("day");
            const isCrewShiftForSelectedDay =
              mCrewShiftStartDay.isSame(mSelectedDate);

            const isFilteredByDepartment =
              !isEmptyList(selectedOptions) &&
              !selectedOptions.includes(crewShiftInfo.departmentUuid);

            const isFilteredBySearch = !isBlank(searchQuery?.searchTerm)
              ? !isSearchQueryMatch(searchQuery?.searchTerm, userInfo.fullName)
              : false;

            const isFiltered = isFilteredByDepartment || isFilteredBySearch;

            if (
              isCrewShiftForSelectedDay &&
              !isNullOrUndefined(crewShiftInfo.departmentUuid) &&
              !(crewShiftInfo.departmentUuid in departmentsMap)
            ) {
              // Keep track of the department
              departmentsMap[crewShiftInfo.departmentUuid] = {
                uuid: crewShiftInfo.departmentUuid,
                name: crewShiftInfo.departmentName,
              };
            }

            if (isCrewShiftForSelectedDay && !isFiltered) {
              crewShiftsList.push(crewShiftInfo);

              if (crewShiftUser.uuid in crewShiftsMap) {
                crewShiftsMap[crewShiftUser.uuid].push(crewShiftInfo);
              } else {
                crewShiftsMap[crewShiftUser.uuid] = [crewShiftInfo];
              }
            }
          }
        }
      }
      sortByField(crewShiftsList, "userInfo.fullName", [
        "departmentName",
        "startTime",
      ]);

      // Also return crewShiftDepartments
      const departmentList = Object.values(departmentsMap);
      sortByField(departmentList, "name");

      return [crewShiftsList, crewShiftsMap, departmentList];
    }, [
      crewShifts,
      users,
      turnaroundsSummary,
      positions,
      airportTimezone,
      selectedDate,
      searchQuery,
      selectedOptions,
    ]);

  useEffect(() => {
    // Fetch other data needed for this page
    getUsers(dispatch);
    getTurnaroundsSummary(dispatch);
    getDepartments(dispatch);
  }, [dispatch]);

  useEffect(() => {
    // Fetch crewShifts for the specified date
    const startTime = selectedDate.toDate().toISOString();
    const endTime = moment(selectedDate).add(1, "day").toDate().toISOString();
    searchCrewShifts(crewShiftsDispatch, startTime, endTime);
  }, [crewShiftsDispatch, selectedDate]);

  // File uploading
  useEffect(() => {
    if (!isNullOrUndefined(fileUpload?.uploadUrl)) {
      const file = fileInputRef.current?.files[0];

      // Reset function
      async function resetAll() {
        setIsUploading(false);
        crewShiftsDispatch({
          type: "setFileUpload",
          value: null,
        });
        fileInputRef.current.value = "";
      }
      if (isNullOrUndefined(file)) {
        // reset
        resetAll();
      } else {
        const reader = new FileReader();
        const fileUploadUuid = fileUpload.uuid;
        const uploadUrl = fileUpload.uploadUrl;
        const fileType = file.type;

        // Function to call once the file bytes are read
        async function fileOnloadHandler() {
          var arrayBuffer = reader.result;
          var bytes = new Uint8Array(arrayBuffer);

          const fileResult = await uploadCrewShiftsFile(
            crewShiftsDispatch,
            uploadUrl,
            bytes,
            fileType
          );
          if (fileResult) {
            // Show the preview to the user before actual import
            const importPreviewResult = await importPreviewCrewShifts(
              crewShiftsDispatch,
              fileUploadUuid
            );
            setImportFileUploadUuid(fileUploadUuid);
            if (!importPreviewResult) {
              resetAll();
            }
            logAnalyticsEvent(
              dispatch,
              ANALYTICS_EVENTS.CREW_SHIFTS_UPLOAD_CSV
            );
          }
          resetAll();
        }
        reader.onload = fileOnloadHandler;

        // Start reading the file bytes
        reader.readAsArrayBuffer(file);
      }
    }
  }, [t, dispatch, crewShiftsDispatch, fileUpload]);

  // This is simply proxy for the setGlobalError on the main dispatch
  useEffect(() => {
    if (!isBlank(globalError)) {
      dispatch({
        type: "setGlobalError",
        globalError: globalError,
        globalErrorList: globalErrorList,
      });
    }
  }, [crewShiftsDispatch, dispatch, globalError, globalErrorList]);

  const crewShiftCount = !isNullOrUndefined(crewShiftsByUserUuid)
    ? Object.keys(crewShiftsByUserUuid).length
    : 0;

  const fileInputRef = useRef(null);
  function handleUploadClick() {
    fileInputRef.current.click();
  }

  function handleUploadFile() {
    setIsUploading(true);
    getFileUpload(crewShiftsDispatch);
    crewShiftsDispatch({
      type: "setGlobalError",
      globalError: null,
      globalErrorList: null,
    });
  }

  function refreshCrewShifts() {
    // Fetch crewShifts for the specified date
    const startTime = selectedDate.toDate().toISOString();
    const endTime = moment(selectedDate).add(1, "day").toDate().toISOString();
    searchCrewShifts(crewShiftsDispatch, startTime, endTime);
  }

  return (
    <div className="crew-shifts">
      <div className="crew-shifts-header">
        <div>
          <div>
            <h3 className="badged">
              {t("crew_shifts")}
              <TitleBadge value={crewShiftCount} />
            </h3>
            <div>
              <span className="crew-shifts-header-detail capitalized">
                {formatLongDate(selectedDate, airportTimezone)}
                {selectedDate.isSame(mToday) && (
                  <>&nbsp;&middot;&nbsp;{t("today")}</>
                )}
              </span>
            </div>
          </div>
          <div className="actions">
            <div>
              {isActive && (
                <button
                  className="secondary"
                  onClick={() => {
                    setIsActive(false);
                    const containerEl = document.querySelector(
                      ".crew-shifts-container"
                    );
                    containerEl.scrollTo(0, 0);
                  }}
                >
                  {t("cancel")}
                </button>
              )}
              {!isActive && (
                <button
                  className="secondary"
                  onClick={() => {
                    setIsActive(true);
                    const containerEl = document.querySelector(
                      ".crew-shifts-container"
                    );
                    containerEl.scrollTo(0, 0);
                    logAnalyticsEvent(
                      dispatch,
                      ANALYTICS_EVENTS.CREW_SHIFTS_ADD_CREW_SHIFT
                    );
                  }}
                >
                  <Plus />
                  {t("add_non_cap")}
                </button>
              )}
            </div>
            <div>
              <button
                className="secondary"
                onClick={() => {
                  window.open(csvTemplate, "_blank");
                  logAnalyticsEvent(
                    dispatch,
                    ANALYTICS_EVENTS.CREW_SHIFTS_DOWNLOAD_CSV_TEMPLATE
                  );
                }}
                disabled={isActive}
              >
                <Download />
                {t("csv_template")}
              </button>
            </div>
            <div>
              <button
                className="secondary"
                onClick={handleUploadClick}
                disabled={isUploading || isActive}
              >
                <Upload />
                {isUploading ? t("uploading") : t("upload_csv")}
              </button>
              <input
                className="not-visible"
                type="file"
                ref={fileInputRef}
                accept=".csv"
                onChange={handleUploadFile}
              />
            </div>
            <div>
              <button
                className="secondary"
                onClick={() => {
                  setIsExportOpen(true);
                }}
                disabled={isActive}
              >
                <Download />
                {t("export_csv")}
              </button>
            </div>
            <div>
              <ButtonFilter
                buttonLabel="filter_departments"
                filterOptions={crewShiftDepartments}
                selectedOptions={selectedOptions}
                onChange={(updatedValues) => {
                  setSelectedOptions(updatedValues);
                  SettingsManager.setSetting(
                    SettingNames.CREW_SHIFT_FILTER_DEPARTMENTS.name,
                    updatedValues.join(",")
                  );
                }}
                disabled={isActive}
              />
            </div>
            <div>
              <ButtonDatePicker
                label={
                  selectedDate == null
                    ? null
                    : formatMediumDate(selectedDate, airportTimezone)
                }
                value={selectedDate}
                onChange={(newValue) => {
                  // Also set search params
                  setSelectedDate(newValue);
                  setSearchParams((prev) => {
                    return {
                      ...prev,
                      date: newValue.format(SEARCH_PARAM_DATE_FORMAT),
                    };
                  });
                }}
                minDate={moment(MIN_DATE)}
                maxDate={moment(mToday).add(2, "month")}
                disabled={isActive}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="crew-shifts-container">
        <div className="crew-shifts-content">
          <CrewShiftsModule
            crewShifts={crewShifts}
            crewShiftsToRender={crewShiftsToRender}
            departments={departments}
            selectedDate={selectedDate}
            onCrewShiftUpdate={() => {
              refreshCrewShifts();
            }}
            isActive={isActive}
            onSetActive={setIsActive}
          />
        </div>
      </div>
      {!isNullOrUndefined(crewShiftsImportPreview) && (
        <CrewShiftPreviewModal
          timezone={airportTimezone}
          crewShiftsImportPreview={crewShiftsImportPreview}
          users={users}
          fileUploadUuid={importFileUploadUuid}
          onCrewShiftImport={() => {
            crewShiftsDispatch({
              type: "setCrewShiftsImportPreview",
              value: null,
            });
            dispatch({
              type: "setAlertMessage",
              alertMessage: t("import_complete"),
            });
            refreshCrewShifts();
          }}
        />
      )}
      {isExportOpen && (
        <CrewShiftExportModal
          isOpen={isExportOpen}
          handleClose={() => {
            setIsExportOpen(false);
          }}
        />
      )}
    </div>
  );
}

export default CrewShifts;
